import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Copy, CheckCircle, ArrowSquareOut, Megaphone } from '@phosphor-icons/react';
import { toast } from 'sonner';
import AdminNav from './AdminNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  not_started: { label: 'Not started', color: '#64748B' },
  submitted:   { label: 'Submitted',   color: '#F59E0B' },
  live:        { label: 'Live',        color: '#10B981' },
  rejected:    { label: 'Rejected',    color: '#EF4444' },
};

const AdminDirectorySubmissions = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.get(`${API_URL}/api/admin/directory-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('Failed to load submissions');
      if (e.response?.status === 401) navigate('/admin/login');
    } finally { setLoading(false); }
  };

  const update = async (id, patch) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/directory-submissions/${id}`, patch, {
        headers: { Authorization: `Bearer ${token}` }
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const copy = (label, text) => {
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${label}`);
  };

  const counts = {
    total: items.length,
    live: items.filter(i => i.status === 'live').length,
    submitted: items.filter(i => i.status === 'submitted').length,
    not_started: items.filter(i => i.status === 'not_started').length,
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <AdminNav title="Directory Submissions" titleTestId="admin-dirsub-title" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="dirsub-page">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Directories" value={counts.total} color="#007AFF" />
          <Stat label="Live" value={counts.live} color="#10B981" />
          <Stat label="Submitted" value={counts.submitted} color="#F59E0B" />
          <Stat label="To do" value={counts.not_started} color="#64748B" />
        </div>

        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 mb-6 flex items-start gap-3">
          <Megaphone size={22} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#CBD5E1]">
            Pre-written submission copy for every directory is in
            <code className="text-cyan-300 mx-1 text-xs">/app/memory/AGENT_DIRECTORY_SUBMISSIONS.md</code>.
            Use the "Copy submission copy" button on each row to grab the blurb for that specific form.
          </div>
        </div>

        {loading ? (
          <p className="text-[#94A3B8] text-center py-8">Loading…</p>
        ) : (
          <div className="space-y-3" data-testid="dirsub-list">
            {items.map(it => (
              <Row key={it.id} item={it} onUpdate={update} onCopy={copy} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4">
    <p className="text-xs text-[#94A3B8] uppercase font-['JetBrains_Mono']">{label}</p>
    <p className="text-3xl font-['JetBrains_Mono'] font-bold" style={{ color }}>{value}</p>
  </div>
);

const Row = ({ item, onUpdate, onCopy }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.notes || '');
  const [submittedUrl, setSubmittedUrl] = useState(item.submitted_url || '');
  const [status, setStatus] = useState(item.status);
  const meta = STATUS_META[status] || STATUS_META.not_started;

  const save = async () => {
    await onUpdate(item.id, { status, notes, submitted_url: submittedUrl });
    toast.success('Saved');
    setExpanded(false);
  };

  return (
    <div className="bg-[#11141D] border rounded-lg transition-colors"
         style={{ borderColor: status === 'live' ? '#10B981' : status === 'submitted' ? '#F59E0B' : '#1E293B' }}
         data-testid={`dirsub-row-${item.id}`}>
      <div className="p-4 flex flex-wrap items-center gap-3 cursor-pointer"
           onClick={() => setExpanded(e => !e)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-['JetBrains_Mono'] font-bold text-cyan-300">{item.name}</p>
            <span className="px-2 py-0.5 rounded text-xs font-bold font-['JetBrains_Mono']"
                  style={{ background: `${meta.color}22`, color: meta.color }}>
              {meta.label}
            </span>
            {item.priority && (
              <span className="text-xs text-[#F59E0B] font-['JetBrains_Mono']">P{item.priority}</span>
            )}
          </div>
          <p className="text-xs text-[#94A3B8] mt-1 truncate">{item.url}</p>
        </div>
        {item.url && (
          <a href={item.url} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             className="text-[#007AFF] hover:text-white p-1"
             data-testid={`open-${item.id}`}
             aria-label="Open directory">
            <ArrowSquareOut size={18} />
          </a>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#1E293B] pt-3" data-testid={`dirsub-expand-${item.id}`}>
          <p className="text-sm text-[#CBD5E1]">{item.description}</p>

          {item.submission_blurb && (
            <div className="bg-[#05050A] rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-['JetBrains_Mono'] text-[#94A3B8]">Submission blurb</span>
                <button
                  onClick={() => onCopy('blurb', item.submission_blurb)}
                  className="text-[#94A3B8] hover:text-white text-xs flex items-center gap-1"
                  data-testid={`copy-blurb-${item.id}`}>
                  <Copy size={12} /> Copy
                </button>
              </div>
              <pre className="text-xs text-[#CBD5E1] whitespace-pre-wrap font-mono">{item.submission_blurb}</pre>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#94A3B8] font-['JetBrains_Mono'] block mb-1">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-transparent border-[#334155]" data-testid={`status-${item.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#94A3B8] font-['JetBrains_Mono'] block mb-1">Submission URL / listing URL</label>
              <Input value={submittedUrl} onChange={(e) => setSubmittedUrl(e.target.value)}
                     placeholder="e.g. https://..."
                     className="bg-transparent border-[#334155]"
                     data-testid={`url-${item.id}`} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#94A3B8] font-['JetBrains_Mono'] block mb-1">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Contact email, ticket #, follow-up date…"
                      className="bg-transparent border-[#334155]"
                      rows={2}
                      data-testid={`notes-${item.id}`} />
          </div>
          <div className="flex justify-end">
            <Button onClick={save} className="bg-[#007AFF]" data-testid={`save-${item.id}`}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDirectorySubmissions;
